import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useAuth } from "../hooks/useAuth";
import {
  emptyUserStats,
  subscribeToUserStats,
  type UserStats,
} from "../services/firebase/statsService";

interface UserStatsContextData {
  stats: UserStats;
  isLoading: boolean;
}

const UserStatsContext = createContext<UserStatsContextData | null>(null);

export function UserStatsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [stats, setStats] = useState<UserStats>(emptyUserStats());
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!user?.uid) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- reset sincrono pre-subscription (padrao anterior ao react-hooks v7); refactor de timing rastreado na auditoria 03/08 par.4.1
      setStats(emptyUserStats());
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const unsub = subscribeToUserStats(
      user.uid,
      (next) => {
        setStats(next);
        setIsLoading(false);
      },
      () => {
        // §BUG-179 adjacente ②: aqui havia `setStats(emptyUserStats())` — um
        // erro do listener APAGAVA da tela a streak e as moedas que já estavam
        // certas. Erro transitório não pode destruir dado bom (a mesma doutrina
        // da guarda de perfil do BUG-177): mantém o último valor conhecido e só
        // assenta o loading. O reset legítimo é o de troca de conta, acima.
        setIsLoading(false);
      },
    );
    return unsub;
  }, [user?.uid]);

  const value = useMemo(
    () => ({
      stats,
      isLoading,
    }),
    [stats, isLoading],
  );

  return <UserStatsContext.Provider value={value}>{children}</UserStatsContext.Provider>;
}

export function useUserStatsContext(): UserStatsContextData {
  const ctx = useContext(UserStatsContext);
  if (!ctx) throw new Error("useUserStatsContext requer UserStatsProvider como ancestral");
  return ctx;
}
